// agent-retention-platform.js - Tier 3 Solution Page
export const content = {
  heroSubtitle: "Built by Medicare agents, for Medicare agents. Finally, retention tools designed for how you actually work—not generic software adapted for insurance.",

  problemSection: {
    title: "Generic Tools Don't Understand Medicare",
    html: `
      <p>I've used a lot of CRMs and retention tools over the years. Salesforce. HubSpot. Industry-specific platforms. They all have the same problem: they weren't built by someone who actually sells Medicare.</p>

      <p>They don't understand AEP and OEP. They don't know what an ANOC letter is. They can't differentiate between MA and Med Supp retention dynamics. They treat Medicare like any other insurance product, when it's really its own world with its own rules.</p>

      <p>The result? You spend more time fighting the tool than using it. You create workarounds for things that should be built-in. You miss retention opportunities because the software doesn't know what matters in Medicare.</p>

      <p>Pro IRP is different because I built it from inside the industry. Every feature exists because I needed it. Every workflow reflects how Medicare agents actually work. It's not adapted—it's native.</p>
    `
  },

  features: [
    {
      icon: "target",
      title: "Medicare-Native Risk Scoring",
      description: "Risk factors that matter for Medicare: plan changes, Star ratings, enrollment timing, dual status. Not generic churn prediction—Medicare-specific intelligence."
    },
    {
      icon: "clock",
      title: "AEP/OEP Aware",
      description: "Workflows built around enrollment periods. Pre-AEP preparation triggers. OEP defense automation. The system knows when your clients are most vulnerable."
    },
    {
      icon: "bell",
      title: "Plan Intelligence Alerts",
      description: "Automatic monitoring for plan changes that affect your clients: ANOC changes, network updates, formulary modifications, Star rating shifts."
    },
    {
      icon: "users",
      title: "Population-Specific Tools",
      description: "Different workflows for different populations: MA, Med Supp, dual eligibles, SNPs. Each population has different retention dynamics—the tool adapts."
    },
    {
      icon: "chart",
      title: "Medicare Retention Metrics",
      description: "Analytics designed for Medicare: first-year retention, AEP churn, OEP switching, segment-specific rates. The numbers that actually matter."
    },
    {
      icon: "shield",
      title: "CMS Compliance Built-In",
      description: "Communication templates and workflows designed with Medicare marketing rules in mind. Stay compliant while staying connected."
    }
  ],

  featuresTitle: "Why Medicare-Specific Matters",

  mainContent: `
    <h2>Built From Inside the Industry</h2>

    <p>I didn't build Pro IRP because I thought there was a market opportunity. I built it because I needed it.</p>

    <p>After years of selling Medicare and watching good agents lose clients for preventable reasons, I was frustrated. The tools available were either too generic (built for all insurance, not Medicare specifically) or too limited (carrier portals that only show their own products).</p>

    <p>I wanted something that:</p>
    <ul>
      <li>Understood the Medicare calendar (AEP, OEP, SEPs)</li>
      <li>Knew about plan changes before my clients did</li>
      <li>Could differentiate MA retention from Med Supp retention</li>
      <li>Worked the way I actually work, not how software designers think I should work</li>
    </ul>

    <p>So I built it. Pro IRP is the tool I wished existed.</p>

    <h2>What "Medicare-Native" Means in Practice</h2>

    <h3>Enrollment Period Awareness</h3>

    <p>Generic CRMs don't know what AEP is. Pro IRP is built around the Medicare calendar:</p>
    <ul>
      <li>Pre-AEP preparation workflows trigger in September</li>
      <li>AEP mode activates October 15 with retention-focused dashboards</li>
      <li>OEP alerts identify first-year clients at risk of switching</li>
      <li>SEP tracking for dual eligibles and qualifying events</li>
    </ul>

    <h3>Plan Type Differentiation</h3>

    <p>MA and Med Supp clients churn for different reasons. Pro IRP treats them differently:</p>
    <ul>
      <li>MA clients: network changes, formulary updates, plan benefit modifications</li>
      <li>Med Supp clients: rate increases, MA comparison conversations, underwriting considerations</li>
      <li>Dual eligibles: quarterly SEP vulnerability, Medicaid coordination</li>
      <li>SNP clients: chronic condition management, care coordination</li>
    </ul>

    <h3>Plan Intelligence Integration</h3>

    <p>Pro IRP monitors the information that matters for retention:</p>
    <ul>
      <li>ANOC changes affecting your clients</li>
      <li>Star rating updates (especially the 3-star SEP threshold)</li>
      <li>Network changes (doctors leaving plans)</li>
      <li>Formulary modifications (drugs dropped or tier-changed)</li>
    </ul>

    <h3>Compliance-Aware Communication</h3>

    <p>Medicare marketing has rules. Pro IRP is designed with them in mind:</p>
    <ul>
      <li>Template language reviewed for CMS compliance</li>
      <li>Scope of appointment tracking</li>
      <li>Contact documentation for audit readiness</li>
      <li>Enrollment period-appropriate messaging</li>
    </ul>

    <h2>Designed for Real Agent Workflows</h2>

    <p>Pro IRP was built by observing how Medicare agents actually work—not how software designers imagine they work.</p>

    <h3>Morning Routine</h3>
    <p>Log in and immediately see:</p>
    <ul>
      <li>Who needs attention today (prioritized by risk)</li>
      <li>New alerts since yesterday</li>
      <li>Scheduled touchpoints due</li>
      <li>Clients with upcoming birthdays or anniversaries</li>
    </ul>

    <h3>Client Conversations</h3>
    <p>Pull up a client and instantly see:</p>
    <ul>
      <li>Their plan details and recent changes</li>
      <li>Contact history and notes</li>
      <li>Risk score and contributing factors</li>
      <li>Upcoming concerns to address</li>
    </ul>

    <h3>AEP Season</h3>
    <p>The dashboard transforms for enrollment season:</p>
    <ul>
      <li>Retention priority list</li>
      <li>Clients with significant plan changes</li>
      <li>Annual review tracking</li>
      <li>Same-day access to plan comparison tools</li>
    </ul>

    <h2>Who Pro IRP Is For</h2>

    <p>Pro IRP works for:</p>
    <ul>
      <li><strong>Solo agents</strong> who need systematic retention without an assistant</li>
      <li><strong>Small agencies</strong> who want consistent processes across agents</li>
      <li><strong>Large agencies/FMOs</strong> who need scalable retention management</li>
    </ul>

    <p>The common thread: you're serious about retention and want tools that understand Medicare specifically.</p>

    <h2>Getting Started</h2>

    <p>Most agents are operational within a day:</p>
    <ol>
      <li>Import your client list (CSV or direct integration)</li>
      <li>System calculates risk scores automatically</li>
      <li>Choose from pre-built Medicare workflows</li>
      <li>Customize to your preferences</li>
      <li>Start working your priority list</li>
    </ol>

    <p>No lengthy implementation. No expensive consultants. Just a tool that works the way you work.</p>

    <h2>The Difference You'll Feel</h2>

    <p>Agents who switch to Pro IRP consistently report:</p>
    <ul>
      <li>Less time on administrative tasks</li>
      <li>More confidence that nothing's falling through the cracks</li>
      <li>Better conversations because they have context at their fingertips</li>
      <li>Higher retention rates within the first year</li>
    </ul>

    <p>The tool doesn't replace your expertise—it amplifies it. You know Medicare. Pro IRP makes sure that knowledge translates into retention.</p>
  `,

  faqs: [
    {
      question: "What makes Pro IRP different from generic CRMs?",
      answer: "Pro IRP is built specifically for Medicare agents. It understands AEP/OEP, monitors plan changes, differentiates MA vs. Med Supp retention dynamics, and includes CMS compliance considerations. Generic CRMs treat Medicare like any other insurance—Pro IRP is native to the industry."
    },
    {
      question: "Do I need to be technical to use Pro IRP?",
      answer: "No. The interface is designed for agents, not IT professionals. Import your clients, choose from pre-built workflows, and start working. If you can use email and a spreadsheet, you can use Pro IRP."
    },
    {
      question: "How does Pro IRP handle different Medicare populations?",
      answer: "Different workflows for different populations: MA clients get network/formulary monitoring, Med Supp clients get rate increase tracking, dual eligibles get quarterly SEP awareness, SNP clients get care coordination features. The tool adapts to each population's retention dynamics."
    },
    {
      question: "Can Pro IRP integrate with carrier systems?",
      answer: "Pro IRP works alongside carrier systems, not in place of them. You'll still use carrier portals for enrollment—Pro IRP handles the retention side. We're working on direct integrations with major carriers for plan data."
    },
    {
      question: "Is Pro IRP compliant with CMS marketing rules?",
      answer: "Pro IRP is designed with CMS compliance in mind. Templates follow marketing guidelines, SOA tracking is built-in, and contact documentation supports audit readiness. That said, you're responsible for ensuring your specific communications comply with current regulations."
    },
    {
      question: "What size book of business is Pro IRP designed for?",
      answer: "Pro IRP scales from solo agents with 100+ clients to large agencies with thousands. The value increases with book size—more clients means more for the system to track and more time saved through systematization."
    }
  ],

  sources: [
    {
      title: "Medicare Advantage in 2024: Enrollment Update and Key Trends",
      url: "https://www.kff.org/medicare/issue-brief/medicare-advantage-in-2024-enrollment-update-and-key-trends/",
      publisher: "Kaiser Family Foundation",
      accessedDate: "2024-12-10"
    }
  ]
};

export default content;
